import { useCallback, useEffect, useRef, useState } from "react";
import type { ContextMenuState, OpenRequest } from "./anchor";
import type { Point, Rect } from "./geometry";
import { handleContextMenu, handleTriggerKeyDown, type TriggerContext } from "./triggerHandlers";
import type { ContextMenuTriggerProps } from "./triggerProps";
import { LONG_PRESS_MS, useLongPress } from "./useLongPress";

export interface UseContextMenuOptions<T> {
  /**
   * Maps the element under the pointer (or the focused element) to what the menu is about.
   * Return null to leave the browser's own menu.
   */
  resolve: (element: Element) => T | null;
  /** While true (e.g. during a drag) the menu does not open and an open one closes. */
  disabled?: boolean;
  /** Touch: how long a press must last to open the menu. */
  longPressMs?: number;
}

function focusedElement(): HTMLElement | null {
  return document.activeElement instanceof HTMLElement ? document.activeElement : null;
}

/**
 * Replaces the browser's context menu inside an area. Spread `triggerProps` on the area's
 * container and render `<ContextMenu state={menu.state} …/>`. Opens on right-click, on
 * Shift+F10 / the Menu key (at the focused element) and on a touch long-press. Text fields
 * keep the browser's menu (copy, paste).
 */
export function useContextMenu<T>({ resolve, disabled = false, longPressMs = LONG_PRESS_MS }: UseContextMenuOptions<T>): {
  state: ContextMenuState<T> | null;
  open: (target: T, anchor: { point: Point; rect?: Rect | null; returnFocus?: HTMLElement | null }) => void;
  close: () => void;
  triggerProps: ContextMenuTriggerProps;
} {
  const [state, setState] = useState<ContextMenuState<T> | null>(null);
  const openCount = useRef(0);
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;
  const resolveLatest = useCallback((element: Element) => resolveRef.current(element), []);

  const open = useCallback((target: T, request: OpenRequest) => {
    openCount.current += 1;
    const returnFocus = request.returnFocus ?? focusedElement();
    setState({ target, point: request.point, rect: request.rect ?? null, returnFocus, openId: openCount.current });
  }, []);
  const close = useCallback(() => setState(null), []);

  const longPress = useLongPress({ delayMs: longPressMs, disabled, resolve: resolveLatest, onLongPress: (target, point) => open(target, { point }) });
  const cancelPress = longPress.cancel;

  useEffect(() => {
    if (!disabled) return;
    cancelPress();
    setState(null);
  }, [disabled, cancelPress]);

  const trigger: TriggerContext<T> = { disabled, resolve: resolveLatest, open, longPress };
  const triggerProps: ContextMenuTriggerProps = {
    onContextMenu: (e) => handleContextMenu(e, trigger),
    onKeyDown: (e) => handleTriggerKeyDown(e, trigger),
    ...longPress.props,
  };

  return { state, open, close, triggerProps };
}
